import { ReactNode, useEffect, useState, useCallback, useMemo } from "react";
import { FeatureFlagsContext, EvaluationContext } from "./context";
import { OpenFeature } from "@openfeature/web-sdk";
import { UserData } from "@/entities/users/types";
import { AccountData } from "@/entities/accounts/types";
import { getFlagsmithClient, setupFeatureFlags } from "./setup";

// Flags settle on their defaults if identify hasn't finished by then, so a stalled
// request or a session that never identifies can't hold every flag consumer in loading.
// A late identify still updates the values once it lands.
export const IDENTIFY_SETTLE_TIMEOUT_MS = 3000;

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children
}) => {
  const [client, setClient] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSettled, setIsSettled] = useState(false);

  useEffect(() => {
    const openFeatureClient = OpenFeature.getClient();
    setClient(openFeatureClient);
  }, []);

  useEffect(() => {
    const timer = setTimeout(
      () => setIsSettled(true),
      IDENTIFY_SETTLE_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, []);

  const getFlag = useCallback(
    async <T,>(
      method: string,
      flagName: string,
      defaultValue: T
    ): Promise<T> => {
      if (!client || !isReady) {
        return defaultValue;
      }

      try {
        return await client[method](flagName.toLowerCase(), defaultValue);
      } catch (e) {
        console.error(`[FeatureFlags] Error getting flag ${flagName}:`, e);
        return defaultValue;
      }
    },
    [client, isReady]
  );

  const isEnabled = useCallback(
    (flagName: string): Promise<boolean> =>
      getFlag<boolean>("getBooleanValue", flagName, false),
    [getFlag]
  );

  const getStringValue = useCallback(
    (flagName: string, defaultValue: string): Promise<string> =>
      getFlag<string>("getStringValue", flagName, defaultValue),
    [getFlag]
  );

  const getNumberValue = useCallback(
    (flagName: string, defaultValue: number): Promise<number> =>
      getFlag<number>("getNumberValue", flagName, defaultValue),
    [getFlag]
  );

  const getBooleanValue = useCallback(
    (flagName: string, defaultValue: boolean): Promise<boolean> =>
      getFlag<boolean>("getBooleanValue", flagName, defaultValue),
    [getFlag]
  );

  const getObjectValue = useCallback(
    (flagName: string, defaultValue: any): Promise<any> =>
      getFlag<any>("getObjectValue", flagName, defaultValue),
    [getFlag]
  );

  const setEvaluationContext = useCallback(
    async (context: EvaluationContext): Promise<void> => {
      try {
        const openFeatureContext: any = {
          targetingKey: context.userId,
          accountId: context.accountId
        };
        await OpenFeature.setContext(openFeatureContext);
      } catch (e) {
        console.error("[FeatureFlags] Error setting evaluation context:", e);
      }
    },
    []
  );

  const identify = useCallback(
    async (user: UserData, account: AccountData) => {
      if (user && account) {
        try {
          // Wait for init before reaching for the client. `App` kicks setup off
          // without awaiting it, so on a fast auth response this can arrive
          // first; without this the call below throws, the catch swallows it,
          // and `isReady` never flips — pinning every flag to its default.
          await setupFeatureFlags();

          // Get the underlying Flagsmith client to call identify() directly
          const flagsmithClient = getFlagsmithClient();

          // Call Flagsmith's identify method directly with traits
          // This is required for segment overrides to work
          await flagsmithClient.identify(user.id, {
            account: account.id
          });

          // Also set OpenFeature context for consistency
          const context = {
            targetingKey: user.id,
            traits: {
              account: account.id
            }
          };

          await OpenFeature.setContext(context);

          // Only update state if not already ready to avoid unnecessary re-renders
          if (!isReady) {
            setIsReady(true);
          }
        } catch (error) {
          console.error("[FeatureFlags] Error during identify:", error);
        } finally {
          setIsSettled(true);
        }
      }
    },
    [isReady]
  );

  const contextValue = useMemo(
    () => ({
      isSettled,
      isEnabled,
      getStringValue,
      getNumberValue,
      getBooleanValue,
      getObjectValue,
      setEvaluationContext,
      identify
    }),
    [
      isSettled,
      isEnabled,
      getStringValue,
      getNumberValue,
      getBooleanValue,
      getObjectValue,
      setEvaluationContext,
      identify
    ]
  );

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
